/**
 * The transport's *record* half is the web bar's **pause** button, so it is
 * imported under that name.
 *
 * Not a pun and not a rename for tidiness. On the desktop one button starts the
 * take and then holds it, and `RecordButton` describes both jobs; the web splits
 * them across two circles, and what is left in that description once the start
 * has gone to the centre button is exactly pause / resume. Importing it as
 * `RecordButton` beside this file's own `RecordButton` would put two unrelated
 * types of that name in one module.
 */
import { transportButtons, type RecordButton as PauseButton } from "@cuekards/ui";
import {
  NOTHING_TO_RECORD,
  canTogglePause,
  isRecordingActive,
  type RecordingPhase,
} from "@cuekards/core/recording";
import {
  rehearseBlocked,
  rehearseLabel,
  type FollowerState,
} from "@cuekards/core/speech";
import { nearestAnchorStop } from "@cuekards/core/overlay-defaults";
import type { ShortcutCommand } from "@cuekards/core/shortcuts";
import type { RefreshAvailability } from "@cuekards/core/refresh";

/**
 * What the floating control bar draws, as data (web-ui ticket 03).
 *
 * The bar has no words on it — five circles and a menu — so every label it
 * carries lives in a `title` and an `aria-label`, where being wrong looks like
 * nothing at all on screen. That is the same argument `transportButtons` was
 * separated out of the desktop's row for, and it is sharper here: the centre
 * button is the visual centre of the whole page and has five appearances rather
 * than two, so a phase that drew the armed button where it meant *stop* would
 * look completely normal until somebody pressed it.
 *
 * So the decisions live here and the component renders them, with
 * `test/control-bar.test.ts` at that seam. Nothing in this file touches the DOM,
 * `window.cuekards`, or a React hook.
 *
 * **It is a second view onto the transport, not a second model of it.** The
 * phase-to-button mapping is `transportButtons`, the shared one; what the web
 * changes is only which half of that row the centre button takes.
 */

/**
 * Why the record button is dead before there is anything to read.
 *
 * **The prompter's sentence, and since flow ticket 04 that is the only screen it
 * is read on.** The prompter has no box, so here it means a script that was never
 * loaded and names a thing to go and do. The green room's own refusal is
 * `EMPTY_BOX_REASON` in `green-room.ts` and is deliberately different: there the
 * box *is* the script and it is two inches away, so a sentence about "loading" is
 * about a control flow ticket 03 deleted.
 */
export const NO_SCRIPT_REASON = "Load or paste a script first";

/**
 * Why the door off the prompter is refused for the length of a take.
 *
 * One string and not two, said once by the `⋯` menu's `edit` row and by the top
 * bar's pill, which are the same door: this is the rule the old chrome applied to
 * "Edit script" with nothing but a `disabled` attribute, which is a refusal whose
 * first explanation was the press that did nothing.
 */
export const MID_TAKE_REASON = "Not while a take is running";

/**
 * The door back to the green room, said once for the two places that say it.
 *
 * The top bar's pill and the `⋯` menu's `edit` row are the same door and answer
 * to the same refusal, so they read one constant rather than two literals that
 * can drift apart. It read "Edit script" until flow ticket 07, which described a
 * screen that had a `Load script` button on it until ticket 05 — what is through
 * the door now is the box the script lives in.
 *
 * Stored **as written** rather than shouted by `text-transform`, which is the
 * rule `START RECORDING` is already stored under: the tests in this repository
 * assert on constants, and a constant saying one thing while the screen says
 * another is a label nothing can check.
 *
 * The static pages held the same rule by shouting `START NEW SESSION` in their own
 * constant until signal-and-paper ticket 06 put that label in sentence case. The
 * rule did not move — `site-copy.test.ts` now holds `site.css` to upper-casing no
 * link and no button, which is the same claim stated from the stylesheet's end.
 * The application's chrome is not on that rope: `web.css` is a different file and
 * these labels are the screen's, not the document's.
 */
export const BACK_TO_SCRIPT = "BACK TO SCRIPT";

/* ---- The centre button ---- */

/** Which of frame 1d's five appearances the record button is drawing. */
export type RecordLook = "no-script" | "ready" | "counting" | "rolling" | "paused";

export interface RecordButton {
  look: RecordLook;
  /** The glyph: a dot while there is a take to start, a square once one is
   * running. Two shapes for two meanings, and never a third for resume — the
   * pause button beside it owns that. */
  icon: "dot" | "stop";
  label: string;
  action: "start" | "stop";
  disabled: boolean;
  /** Why it is dead, where that is a thing a user can act on. The countdown and
   * the save are self-evident from the screen; an unloaded script is not. */
  reason?: string;
}

export interface RecordInputs {
  phase: RecordingPhase;
  /** Whether there is a script loaded to record against. */
  loaded: boolean;
  /**
   * Whether any device is switched on to record with (web-ui ticket 06).
   *
   * Defaulted to true, because it is a fact only the green room's host has and
   * three of this file's callers are tests about the phases. A screen that does
   * not know cannot have switched anything off.
   */
  anyDevice?: boolean;
}

/**
 * The record button, for the phase it is in.
 *
 * `starting` draws the counting look rather than the rolling one, and that is the
 * one mapping in here worth stating: it is the gap between the last digit and the
 * recorder actually rolling, so the camera is still opening and the ring is still
 * animating — but the press behind it is already a stop that saves, which is
 * `transportButtons`' answer and not a judgement made here.
 *
 * `stopping` draws the rolling look and is dead, for the same reason the desktop's
 * stop is: the file is already being written and there is nothing left for a
 * second press to do. Removing the button instead would take the visual centre out
 * of the page at the moment the user is watching it hardest.
 */
export function recordButton({
  phase,
  loaded,
  anyDevice = true,
}: RecordInputs): RecordButton {
  const { record, stop } = transportButtons(phase);
  if (phase === "idle") {
    // The script comes first when both are missing: it is the thing the visitor
    // came here to do, and a room with no script in it is the ordinary state of
    // a room, where both devices switched off is something somebody just did.
    // `NOTHING_TO_RECORD` is `RecordingController`'s own refusal rather than a
    // second sentence of this file's: the bar is not the only way into a take, so
    // the `R` key and the spoken "start recording" are refused there, and a
    // tooltip that named a different thing to go and do would be the two of them
    // disagreeing about the same state.
    const refusal = !loaded
      ? NO_SCRIPT_REASON
      : anyDevice
        ? null
        : NOTHING_TO_RECORD;
    return {
      look: refusal === null ? "ready" : "no-script",
      icon: "dot",
      label: record.label,
      action: "start",
      disabled: refusal !== null,
      ...(refusal === null ? {} : { reason: refusal }),
    };
  }
  return {
    look: LOOK_WHILE_ACTIVE[phase],
    icon: "stop",
    label: stop.label,
    action: "stop",
    disabled: stop.disabled,
  };
}

/** The look each running phase draws. Written whole rather than derived, so a
 * phase added to the core is a compile error here rather than a button that
 * silently draws the wrong one. */
const LOOK_WHILE_ACTIVE: Record<
  Exclude<RecordingPhase, "idle">,
  Exclude<RecordLook, "no-script" | "ready">
> = {
  counting: "counting",
  starting: "counting",
  recording: "rolling",
  paused: "paused",
  stopping: "rolling",
};

/* ---- The pause beside it ---- */

/**
 * The pause button, or null while there is nothing for it to hold.
 *
 * It is `transportButtons`' *record* half — see the note on the import above for
 * why that is the pause. The description comes from there rather than being
 * restated, `canTogglePause` included, which is the rule the engine itself
 * applies before acting.
 *
 * Present and greyed rather than absent for the rest of a take, so the control
 * does not appear and disappear under a hand already reaching for it. It slides in
 * only when a take begins, which is the one appearance the brief asks for.
 */
export function pauseButton(phase: RecordingPhase): PauseButton | null {
  if (!isRecordingActive(phase)) return null;
  const { record } = transportButtons(phase);
  return { ...record, disabled: !canTogglePause(phase) };
}

/* ---- The more menu ---- */

/**
 * A row in the `⋯` menu.
 *
 * Twelve of them, which is most of what the old top-left chrome carried plus the
 * keyboard commands that had no button anywhere. The four things you reach for
 * while a camera is pointed at you are on the bar; everything else is one press
 * further away, which is the whole shape of this redesign. It was eleven until
 * text-align ticket 01, which is the first row here that answers to no button
 * and no key anywhere else — the `⋯` was chosen for it precisely because
 * anything else would have been a control on a bar for a thing set once a desk.
 *
 * There was another, `help`, until flow ticket 07. It was the last row in this
 * product that reached a page through a callback rather than an `<a>`, and the
 * `?` in the corner of this very bar has been an anchor at the same address since
 * ticket 02 — so it was a second way to somewhere that had a first one.
 */
export type MenuCommand =
  | "rehearse"
  | "restart"
  | "nudgeBack"
  | "nudgeForward"
  | "resync"
  | "cycleMode"
  | "fontDown"
  | "fontUp"
  | "textAlign"
  | "refresh"
  | "edit"
  | "settings";

/** The order the brief gives, and the order the panel renders. Exported so the
 * test sweeps the shipped list rather than a copy of it.
 *
 * `textAlign` (text-align ticket 01) goes after the two font rows and not with
 * the four above them, which is the one placement decision in this list worth
 * stating: nudging, re-syncing and going back to the top move the *reader*
 * through the script, and the font and the alignment change how the script is
 * drawn. It is one row and no icon, which is the whole reason the `⋯` was the
 * right place for it — the bar has no room for a sixth circle nobody presses
 * twice a week. */
export const MENU_ORDER: readonly MenuCommand[] = [
  "rehearse",
  "restart",
  "nudgeBack",
  "nudgeForward",
  "resync",
  "cycleMode",
  "fontDown",
  "fontUp",
  "textAlign",
  "refresh",
  "edit",
  "settings",
] as const;

export interface MenuRow {
  command: MenuCommand;
  label: string;
  /**
   * The shortcut whose chord is shown on the right of the row, where there is
   * one.
   *
   * A *command* and never a letter: keys are rebindable, so the chord is looked
   * up live through `useShortcutBindings` at the moment the menu is drawn. A row
   * that named "M" in this table would go on saying "M" after somebody moved it.
   */
  chord?: ShortcutCommand;
  disabled: boolean;
  /**
   * The row's `title` — the only place a dimmed row can explain itself.
   *
   * Present on a *live* row only where the helper behind it says something worth
   * saying either way, which is refresh alone: `refreshAvailability` answers
   * "you are recording" and "re-fetch and keep your place" out of the same field,
   * and dropping the second half would take a sentence off the screen that the
   * old chrome's button carried.
   */
  reason?: string;
}

export interface MenuInputs {
  phase: RecordingPhase;
  follower: FollowerState;
  /**
   * Whether the user has switched the microphone off (web-ui ticket 06).
   *
   * Rehearse's own rule needs it, and it comes from the same device state the
   * bar's two circles are drawn from rather than from a second reading: a menu row
   * that disagreed with the circle three pixels away about whether the microphone
   * is on would be worse than either answer on its own.
   */
  micOff?: boolean;
  /**
   * Where the reading line currently sits, as a fraction of the prompter's
   * height (text-align ticket 01) — what {@link alignLabel} names on the row.
   *
   * Required rather than defaulted, unlike `micOff` above it. A default would be
   * a screen quietly claiming the middle stop while the words were at the top,
   * and the label is the only place this setting is ever visible: there is no
   * icon on the bar and no row on the settings screen to disagree with it.
   */
  anchor: number;
  /** Refresh's own rule (FR-17), decided by `refreshAvailability` and passed in
   * rather than restated here. */
  availability: RefreshAvailability;
}

/**
 * What the alignment row says, for the stop the prompter is on.
 *
 * The label carries the value, which is `rehearseLabel`'s arrangement and is
 * here for the same reason: a row whose words never change is a row that can
 * only tell you what pressing it does, and this one has to tell you where you
 * already are. Cycling is watching the words move, and the label is what says
 * you have come round to where you started.
 *
 * Plain `Top` / `Middle` / `Bottom` rather than percentages, because 12% is not
 * a thing anybody knows about their own desk. The snap is `nearestAnchorStop`,
 * the core's own — the same call `cycleAnchor` makes, so a stored value that
 * never came through this menu is named as the stop the next press will move on
 * from rather than as a fourth thing the control cannot reach. A second copy of
 * that arithmetic here is exactly how a row comes to name one stop while the
 * press moves on from another.
 */
export function alignLabel(anchor: number): string {
  return `Text align: ${ALIGN_NAMES.get(nearestAnchorStop(anchor)) ?? UNNAMED}`;
}

/**
 * The word for each stop.
 *
 * Keyed by the position rather than laid out in the same order as
 * `ANCHOR_STOPS`, because a name and a position are two different kinds of fact:
 * one is this package's copy, where every other label on this screen lives, and
 * one is the core's. Keying by value is what makes a stop the core has *moved*
 * fall out as a stop with no name, which is a thing the test beside this file
 * can see — an index-aligned list would have gone on confidently naming it.
 */
const ALIGN_NAMES = new Map<number, string>([
  [0.12, "Top"],
  [0.33, "Middle"],
  [0.75, "Bottom"],
]);

/** What the row says about a stop this file has no word for — which is a state
 * the test refuses, not one anybody should reach. It exists so `alignLabel` can
 * be total without pretending an unknown position is the middle one. */
const UNNAMED = "—";

/**
 * The menu, for the state the page is in.
 *
 * Three kinds of row, and the difference is which rule dims them:
 *
 * - **The position and display commands are never dimmed.** Moving the reader
 *   through the script, changing the font size, and moving the reading line down
 *   the screen are all things the prompter can always do, including during a take
 *   somebody has decided to restart from the top. The last of those is the newest
 *   and the sharpest case for the rule: a reader three sentences in who realises
 *   they are looking below their lens should be able to fix it rather than stop.
 * - **Rehearse and refresh carry somebody else's rule** — `rehearseBlocked` and
 *   `refreshAvailability` — so the menu cannot disagree with the keyboard or with
 *   the engine about what is allowed.
 * - **The door back to the room leaves the screen you are on**, and a take you
 *   walked off the screen to edit a script is a take you have lost. One direction
 *   since flow ticket 04: the bar is the prompter's alone, and the room's own
 *   `START RECORDING` is what comes the other way. How it works answered to the
 *   same rule until flow ticket 07 took that row out, which leaves `leaving` with
 *   one user — kept as a named binding rather than inlined, because it is a rule
 *   about walking off a screen and not a property of one row. Settings is
 *   deliberately not one of them: nothing on that screen is refused mid-take that is not
 *   refused on its own screen, and a prompter whose only door is locked while it
 *   is recording is worse than the screen behind it.
 */
export function menuRows({
  phase,
  follower,
  anchor,
  availability,
  micOff = false,
}: MenuInputs): MenuRow[] {
  const blocked = rehearseBlocked(follower, phase, micOff);
  const takeInProgress = isRecordingActive(phase);
  const leaving = takeInProgress
    ? { disabled: true, reason: MID_TAKE_REASON }
    : { disabled: false };

  const rows: Record<MenuCommand, Omit<MenuRow, "command">> = {
    rehearse: {
      label: rehearseLabel(follower),
      disabled: blocked !== null,
      ...(blocked === null ? {} : { reason: blocked }),
    },
    restart: { label: "Back to top", chord: "restart", disabled: false },
    nudgeBack: { label: "Nudge back", chord: "nudgeBack", disabled: false },
    nudgeForward: { label: "Nudge forward", chord: "nudgeForward", disabled: false },
    resync: { label: "Re-sync", chord: "resync", disabled: false },
    cycleMode: { label: "Cycle mode", chord: "cycleMode", disabled: false },
    fontDown: { label: "Font size −", chord: "fontDown", disabled: false },
    fontUp: { label: "Font size +", chord: "fontUp", disabled: false },
    /* No chord: text-align ticket 01 deliberately gave this no keyboard reach.
     * A chord is for the things you do without looking, and where the words sit
     * is answered once per desk by pressing this and watching them move. */
    textAlign: { label: alignLabel(anchor), disabled: false },
    refresh: {
      label: "Refresh script",
      chord: "refresh",
      disabled: !availability.enabled,
      reason: availability.reason,
    },
    edit: { label: BACK_TO_SCRIPT, ...leaving },
    settings: { label: "Settings", disabled: false },
  };

  return MENU_ORDER.map((command) => ({ command, ...rows[command] }));
}

/* ---- What a control says when you point at it ---- */

/**
 * A control's label and its current key, as one line — `Start recording · R`.
 *
 * The key half is absent when there is no binding to read, and four of the bar's
 * six circles are in that case: mic and camera are not in the shortcut table (the
 * green room turns them on and off with a press, and adding keys for them is
 * ticket 06's business), the menu is not a command, and full screen is `F11`, which
 * `useShortcuts` deliberately leaves to the browser rather than fighting it. A
 * tooltip that named a key those controls do not have would be worse than one that
 * says nothing.
 */
export function tooltip(label: string, chord?: string): string {
  return chord === undefined ? label : `${label} · ${chord}`;
}
